import { ParticipationReportResponse, MultiUserReportResponse, VenueOccupancyReport, TableOccupancyReport } from '../types/report';
import api from './authService';
import { apiErrorMessage, responseErrorMessage } from '../utils/apiErrors';

/**
 * ════════════════════════════════════════════════════════════════════════════════════════
 * LETÖLTÉS-SEGÉD — a `responseType: 'blob'` MIATT A HIBAÜZENET ELNYELŐDÖTT [2026-09-14]
 * ════════════════════════════════════════════════════════════════════════════════════════
 * MI VOLT A BAJ: minden export `responseType: 'blob'`-bal hív, ezért a HIBA-válasz törzse is
 * `Blob` lesz, nem objektum. A hívó (`Reports.tsx`) `err.response?.data?.message`-t olvas →
 * `undefined`, tehát a `err.message` fallback futott, ami az axios ANGOL mondata:
 * „Request failed with status code 404". MÉRVE (2026-09-14): a játékos-export végpont nem is
 * létezett, tehát az admin a végig magyar felületen pontosan ezt az angol mondatot látta.
 * (A végpont hiánya külön javítva: `ReportsController.cs` → `ExportPlayerParticipationReport`.)
 *
 * HOGYAN: a hiba-Blobot VISSZAFEJTJÜK szövegre, és ha JSON `message`-t tartalmaz (a backend
 * minden export-hibája ilyen), AZT dobjuk tovább. Ha nem értelmezhető, MAGYAR mondatot dobunk —
 * angol axios-szöveg semmilyen úton nem juthat ki a felületre.
 */
/**
 * Egy `Blob` tartalmának kiolvasása szövegként.
 * ⚠️ NEM elég a `blob.text()` [MÉRVE 2026-09-14]: a teszt-környezet jsdom-jában
 * `typeof Blob.prototype.text === 'undefined'`, és régebbi böngészőkben sem garantált. A
 * `FileReader` mindenhol létezik, ezért az a tartalék — enélkül a visszafejtés csendben elbukott
 * volna, és pont a MAGYAR szerver-üzenet maradt volna el (a teszt ezt ki is fogta).
 */
async function blobToText(blob: Blob): Promise<string> {
  if (typeof (blob as any).text === 'function') {
    return await (blob as any).text();
  }
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('A hiba részletei nem olvashatók'));
    reader.readAsText(blob);
  });
}

/**
 * Az export-hiba MAGYAR szövege [átírva: Web UX 3. csomag (C), 2026-10-04 — 2. validátor-kör F1].
 *
 * MOST A KÖZÖS MINTÁN FUT (`utils/apiErrors.ts`), két korábbi rés bezárásával:
 *  1. A Blob-törzs nem-JSON szövegét (ha 300 karakternél rövidebb) NYERSEN átengedtük. A backend
 *     minden hibája JSON, tehát sima szöveg csak infrastruktúrától jöhet — ANGOLUL (élesben mérve:
 *     `Event not found`). A `responseErrorMessage` ezt nem engedi ki, a magyar tartalék megy
 *     a HTTP-kóddal. Ugyanott a modell-validációs `errors` alak is olvasva van.
 *  2. Hálózati hibánál (nincs `response`) eddig csak a tartalék ment ki — a felhasználó nem tudta,
 *     hogy a szerver el sem érhető. Most az `apiErrorMessage` a „Nem sikerült elérni a
 *     szervert…" mondatot adja (valódi axios-hibára; programhibára a tartalékot).
 */
async function messageFromBlobError(err: any, fallback: string): Promise<string> {
  const data = err?.response?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    let text = '';
    try {
      text = await blobToText(data);
    } catch {
      // A Blob nem olvasható — a magyar tartalék lép be (a HTTP-kóddal).
    }
    return responseErrorMessage(text, err?.response?.status ?? 0, fallback);
  }
  return apiErrorMessage(err, fallback);
}

/**
 * Egy CSV/bináris válasz letöltése fájlként. Hibánál MAGYAR mondattal dobja tovább a hibát —
 * lásd `messageFromBlobError`.
 */
async function downloadFile(url: string, params: any, fileName: string, errorFallback: string): Promise<void> {
  let response;
  try {
    response = await api.get(url, { params, responseType: 'blob' });
  } catch (err: any) {
    // A kész MAGYAR szöveg `userMessage`-ként is ott van [2026-10-04]: a hívó (`Reports.tsx`
    // `exportErrorMessage`) ebből tudja, hogy a mondat már feldolgozott, felhasználónak szóló —
    // egy sima `Error` `message`-ét a közös minta (helyesen) nem írja ki.
    const message = await messageFromBlobError(err, errorFallback);
    const wrapped: any = new Error(message);
    wrapped.userMessage = message;
    throw wrapped;
  }

  const objectUrl = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = objectUrl;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
}

export const reportService = {
  async getPlayerParticipationReport(userId: string, year: number, month: number): Promise<ParticipationReportResponse> {
    const response = await api.get(`/reports/player-participation/${userId}`, {
      params: { year, month }
    });
    return response.data;
  },

  async getCoachParticipationReport(userId: string, year: number, month: number, startDate?: string, endDate?: string): Promise<ParticipationReportResponse> {
    const params: any = { year, month };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await api.get(`/reports/coach-participation/${userId}`, {
      params
    });
    return response.data;
  },

  // ⚠️ A KITERJESZTÉS `.csv`, NEM `.xlsx` [javítva 2026-09-14]: a szerver CSV-t ad (a többi
  // export is), az `.xlsx` név miatt az Excel „a fájl formátuma nem egyezik a kiterjesztéssel"
  // figyelmeztetést adott volna. A végpont maga is most készült el — korábban 404 volt (mérve).
  async exportPlayerReport(userId: string, year: number, month: number): Promise<void> {
    await downloadFile(
      `/reports/player-participation/${userId}/export`,
      { year, month },
      `jatekos-reszvetel-${year}-${month.toString().padStart(2, '0')}.csv`,
      'Nem sikerült exportálni a játékos részvételi kimutatását'
    );
  },

  async exportCoachReport(userId: string, year: number, month: number): Promise<void> {
    await downloadFile(
      `/reports/coach-participation/${userId}/export`,
      { year, month },
      `edzo-reszvetel-${year}-${month.toString().padStart(2, '0')}.csv`,
      'Nem sikerült exportálni az edző részvételi kimutatását'
    );
  },

  // Multi-user reports
  async getAllPlayersParticipationReport(year: number, month: number): Promise<MultiUserReportResponse> {
    const response = await api.get('/reports/multi-user-participation/players', {
      params: { year, month }
    });
    return response.data;
  },

  async getAllCoachesParticipationReport(year: number, month: number, startDate?: string, endDate?: string): Promise<MultiUserReportResponse> {
    const params: any = { year, month };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await api.get('/reports/multi-user-participation/coaches', {
      params
    });
    return response.data;
  },

  async exportAllPlayersReport(year: number, month: number): Promise<void> {
    await downloadFile(
      '/reports/multi-user-participation/players/export',
      { year, month },
      `osszes-jatekos-reszvetel-${year}-${month.toString().padStart(2, '0')}.csv`,
      'Nem sikerült exportálni a játékosok összesített kimutatását'
    );
  },

  async exportAllCoachesReport(year: number, month: number): Promise<void> {
    await downloadFile(
      '/reports/multi-user-participation/coaches/export',
      { year, month },
      `osszes-edzo-reszvetel-${year}-${month.toString().padStart(2, '0')}.csv`,
      'Nem sikerült exportálni az edzők összesített kimutatását'
    );
  },

  // Venue occupancy reports
  async getVenueOccupancyReport(startDate?: string, endDate?: string): Promise<VenueOccupancyReport> {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await api.get('/reports/venue-occupancy', { params });
    return response.data;
  },

  async exportVenueOccupancyReport(startDate?: string, endDate?: string): Promise<void> {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    await downloadFile(
      '/reports/venue-occupancy/export',
      params,
      `helyszin-fogaltsag-${new Date().toISOString().split('T')[0]}.csv`,
      'Nem sikerült exportálni a helyszín-kihasználtsági kimutatást'
    );
  },

  // Table occupancy reports
  async getTableOccupancyReport(date: string, venueId: string): Promise<TableOccupancyReport> {
    const response = await api.get('/reports/table-occupancy', {
      params: { date, venueId }
    });
    return response.data;
  },

  async exportTableOccupancyReport(date: string, venueId: string): Promise<void> {
    await downloadFile(
      '/reports/table-occupancy/export',
      { date, venueId },
      `asztal-fogaltsag-${date}.csv`,
      'Nem sikerült exportálni az asztal-kihasználtsági kimutatást'
    );
  }
};